
import React, { useState, useEffect } from "react";
import { Modal, Form, Input, InputNumber, DatePicker, Select, Button, message } from "antd";
import { expensesService } from "../../services/api.service";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import logger from "../../utils/logger";
import {
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;

const EditExpenseModal = ({ visible, onClose, onSuccess, expenseData, caseData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    expense_date: dayjs(),
  });

  // أنواع المصاريف المتاحة
  const expenseTypes = [
    "رسوم قضائية",
    "أتعاب محاماة",
    "رسوم ترجمة",
    "تكاليف سفر",
    "طباعة وتصوير",
    "تكاليف أخرى"
  ];

  useEffect(() => {
    if (visible && expenseData) {
      form.setFieldsValue({
        case_id: caseData?.id || expenseData.case_id,
        expense_type: expenseData.expense_type,
        amount: expenseData.amount,
        expense_date: dayjs(expenseData.expense_date),
        description: expenseData.description,
        receipt_number: expenseData.receipt_number,
        notes: expenseData.notes,
      });
      setFormData({
        expense_date: dayjs(expenseData.expense_date),
      });
    }
  }, [visible, expenseData, caseData, form]);

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // تحويل التاريخ إلى الصيغة المطلوبة
      const formattedValues = {
        ...values,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
      };

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await expensesService.updateExpense(expenseData.id, formattedValues);

      // Show success message with SweetAlert2
      await Swal.fire({
        title: "تم التعديل!",
        text: "تم تعديل المصروف بنجاح",
        icon: "success",
        timer: 1500,
      });

      form.resetFields();
      onSuccess();
    } catch (error) {
      logger.error("Error updating expense:", error);

      // Show error message with SweetAlert2
      Swal.fire({
        title: "خطأ!",
        text:
          error.response?.data?.message ||
          error.message ||
          "فشل في تعديل المصروف",
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      expense_date: date,
    }));
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-right text-gray-800 mb-4 flex items-center gap-2">
          <FileProtectOutlined className="text-primary" />
          تعديل المصروف
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      okText="حفظ التعديلات"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={submitting}
      width={800}
      dir="rtl"
      className="custom-modal"
      styles={{ body: { maxHeight: "80vh", overflow: "auto", padding: "20px" } }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="px-4"
        requiredMark={false}
        dir="rtl"
        onValuesChange={(changedValues) => {
          // Clear specific errors when fields change
          if ("expense_type" in changedValues && changedValues.expense_type) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated.expense_type;
              return updated;
            });
          }

          if ("amount" in changedValues && changedValues.amount) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated.amount;
              return updated;
            });
          }
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Form.Item
          name="case_id"
          label="رقم القضية"
          hidden
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="expense_type"
          label={
            <span className="text-gray-800 font-semibold flex items-center gap-1">
              <InfoCircleOutlined className="text-primary" />
              نوع المصروف <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: "يرجى اختيار نوع المصروف" }]}
          className="mb-3"
        >
          <Select
            placeholder="اختر نوع المصروف"
            className="text-right h-11"
            size="large"
            suffixIcon={<InfoCircleOutlined className="text-primary" />}
          >
            {expenseTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="amount"
          label={
            <span className="text-gray-800 font-semibold flex items-center gap-1">
              <DollarOutlined className="text-primary" />
              المبلغ <span className="text-red-500">*</span>
            </span>
          }
          rules={[
            { required: true, message: "المبلغ مطلوب" },
            {
              type: "number",
              min: 1,
              transform: (value) => Number(value),
              message: "المبلغ يجب أن يكون أكبر من صفر",
            },
          ]}
          className="mb-3"
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="أدخل المبلغ"
            min={0}
            size="large"
            className="text-right h-11"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            addonAfter="د.ع"
          />
        </Form.Item>

        <Form.Item
          name="expense_date"
          label={
            <span className="text-gray-800 font-semibold flex items-center gap-1">
              <CalendarOutlined className="text-primary" />
              تاريخ المصروف <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: "يرجى اختيار تاريخ المصروف" }]}
          className="mb-3"
        >
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY/MM/DD"
            placeholder="اختر التاريخ"
            size="large"
            className="text-right h-11"
            value={formData.expense_date}
            onChange={handleDateChange}
            suffixIcon={<CalendarOutlined className="text-primary" />}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={
            <span className="text-gray-800 font-semibold flex items-center gap-1">
              <FileTextOutlined className="text-primary" />
              الوصف
            </span>
          }
          className="mb-3 md:col-span-2"
        >
          <TextArea
            rows={3}
            placeholder="أدخل وصف المصروف"
            maxLength={500}
            showCount
            className="text-right"
          />
        </Form.Item>

        <Form.Item
          name="receipt_number"
          label={
            <span className="text-gray-800 font-semibold flex items-center gap-1">
              <FileProtectOutlined className="text-primary" />
              رقم الإيصال
            </span>
          }
          className="mb-3"
        >
          <Input
            placeholder="أدخل رقم الإيصال (اختياري)"
            size="large"
            className="text-right h-11"
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label={
            <span className="text-gray-800 font-semibold flex items-center gap-1">
              <InfoCircleOutlined className="text-primary" />
              ملاحظات
            </span>
          }
          className="mb-3 md:col-span-2"
        >
          <TextArea
            rows={2}
            placeholder="أدخل أي ملاحظات إضافية"
            maxLength={300}
            showCount
            className="text-right"
          />
        </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default EditExpenseModal;
